/**
 * Quarterly access review (Vlad, 2026-07-21).
 *
 * Nobody wants to own the nurse list. Alex does not want to add and remove
 * nurses by hand, and neither do Helen or Owen — so the list rots, and a
 * rotting access list at a nursing home is exactly the kind that still has
 * last year's agency staff on it. The answer is not a person remembering:
 * it is a recurring, boring table sent to someone at the home who DOES
 * know who still works there, with one column for them to fill in.
 *
 * Cadence (Vlad's call): quarterly to the home's administrator, and the
 * same report to Alex monthly so he or his automations can act between
 * reviews.
 *
 * This module only BUILDS the report. It deliberately does not send it:
 * email needs the Resend key and a verified sending domain, and in
 * synthetic mode a Resend key is a fatal startup error by design. Wiring
 * the send is a separate step once Alex has the domain — see
 * docs/OPEN-QUESTIONS.md.
 *
 * Two properties matter more than anything else here, and both are pinned
 * in tests: the report lists EVERY nurse (a recertification list that
 * quietly omits someone is worse than none), and it carries no secrets and
 * no resident data — it is an access list, not a data dump.
 *
 * ⛔ "EVERY NURSE" NOW MEANS EVERY NURSE (T788 M4). This said "every ACTIVE
 * nurse" and the query carried `WHERE n.active = true`, which quietly
 * excluded the single population an access review most needs to show: the
 * people whose access was already removed. The reviewer could not tell
 * "she was removed" from "she was never here", so the removal could not be
 * confirmed — only trusted. Deactivated nurses now appear, carrying
 * `active: false`, and the surfaces that render them say so.
 */
import type pg from 'pg';
import { csvCell } from './csv.js';
import type { PortalRole } from '../shared/roles.js';

/**
 * ⭐ T2075 M5 (plan Step 2) — `csvCell` LIVES IN `core/csv.ts` NOW and is
 * re-exported here BY IDENTITY, so the staff export, the home's own review and
 * the operators' cross-home review neutralise a formula lead the same way. Its
 * reachability note moved with it.
 */
export { csvCell };

/** How long without activity before a nurse is flagged for the reviewer's
 *  attention. A quarter plus a little slack: someone who has not touched
 *  the portal in a full review cycle is the first thing the administrator
 *  should look at. Not an expiry — the human still decides. */
export const DORMANT_AFTER_DAYS = 100;

export type AccessReviewNurse = {
  /**
   * The row's own key (T939 M5).
   *
   * ⛔ ADDED BECAUSE THE REVOKE CONTROL HAD NOTHING TO ADDRESS. This report is
   * the ONLY read on the ops seam that lists nurses, and until now it returned
   * no id at all — so a kiosk screen could render "Remove access for Mary" and
   * had no way to call `POST /api/ops/nurses/:id/active` when it was pressed.
   * `email` is UNIQUE system-wide and would have worked as a join key, but the
   * write route addresses by `nurse_id`, and resolving one to the other in a
   * browser would mean a second lookup over the exact population this report
   * exists to minimise.
   *
   * ⚠️ It is an internal row id, NOT a new personal field: `nurse_id` already
   * crosses the same seam on every write response (the kiosk's
   * `PORTAL_NURSE_KEEP_KEYS`). The keep-set widening it forces on the kiosk
   * side is therefore about REACHABILITY, not about carrying more data about a
   * person. It is deliberately NOT a column of the emailed CSV.
   */
  nurseId: number;
  displayName: string;
  email: string;
  /** Has ever completed a magic-code sign-in (i.e. has a session on record). */
  hasSignedIn: boolean;
  /** Has set a PIN, so they can quick-switch on a device shared by the home.
   *  (Not "a shared iPad" — Alex ruled 2026-08-04 that the device is chosen per
   *  home and spans phones, home-issued devices, iPads and reception desktops.
   *  What matters here is that the device is SHARED, not what it is.) */
  hasPin: boolean;
  /** ISO timestamp of last session activity, or null if never signed in. */
  lastActiveAt: string | null;
  /** Number of distinct devices this nurse is enrolled on. */
  deviceCount: number;
  /** No activity within DORMANT_AFTER_DAYS (true also when never signed in). */
  dormant: boolean;
  /**
   * Whether this nurse can still sign in (T788 M4).
   *
   * ⛔ THE REPORT USED TO OMIT `active = false` ENTIRELY, and that was the
   * single worst thing about it: an access review exists to answer "who should
   * still have access?", and the population it most needs to show is the one
   * whose access was already taken away — so the reviewer can confirm the
   * removal happened rather than take it on trust. A list that silently drops
   * them cannot distinguish "she was removed" from "she was never on it".
   */
  active: boolean;
  /**
   * ⭐ T2075 M5 (plan D9, A7, S17) — WHAT THE ACCOUNT MAY SEE. `role` is the
   * live value off `portal.nurses`; `groupLabel` is a `group` account's scope
   * key (null for reception / admin and for a pre-v13 account). A review that
   * lists who can sign in without listing what each sign-in reaches cannot
   * answer R-8's question, and the R-9 flag (below, on the home) only means
   * anything beside a role.
   */
  role: PortalRole;
  groupLabel: string | null;
};

/**
 * ⭐ T2075 M5 (plan Step 7, D9) — ONE ACT ON THE HOME'S ACCESS, from either
 * trail: `nurse_access_events` (an account added, deactivated, reactivated or
 * its PIN reset) or `staff_events` (a roster row added, renamed, regrouped,
 * deactivated or reactivated). The actor string is what the trail holds —
 * `admin:<nurse_id>` from the admin door, `nurse:<nurse_id>` from the request
 * form's add-a-name (board 2732 M4, any role), an operator's name from the
 * kiosk's passcode door — and `actorNurseId` is the session-derived integer beside it
 * (null on every passcode-door row). `target` is the surrogate the trail
 * carries: a nurse's work email on the account trail, a `staff:<id>` on the
 * roster trail, which carries NO name by design (migration v13's comment).
 */
export type AccessReviewEvent = {
  trail: 'account' | 'staff';
  kind: string;
  actor: string;
  actorNurseId: number | null;
  target: string;
  at: string;
};

export type AccessReviewHome = {
  /**
   * ⛔ T1146 M2 — THE JOIN KEY, and the reason this report can be trusted.
   *
   * `portal.homes.name` has NO unique constraint and the cutover is what makes
   * duplicates (the kiosk's "Bring X in" MINTS a home named after the account).
   * Grouped by NAME, two homes collapsed into one entry and the kiosk — which
   * iterates its own home list, one row per `home_id` — rendered that merged
   * list under BOTH rows: each nurse shown twice, and exported twice, on an
   * Article 9 access review. A security review measured it.
   *
   * ⚠️ A REACHABILITY KEY, NOT A FACT ABOUT A PERSON. It is the same widening
   * `nurseId` took, and it reveals strictly less than `homeName`, which already
   * crosses this seam. It is deliberately NOT a column of the emailed CSV: that
   * sheet goes to a third party (the home's administrator) and a surrogate id
   * means nothing to them — the identical rule `nurseId` is held to.
   */
  homeId: number;
  homeName: string;
  nurses: AccessReviewNurse[];
  /**
   * ⭐ T2075 M5 (plan D10, S9, A7) — THE R-9 FLAG AND WHEN IT WAS SET, read
   * off `portal.homes` (its writer is the provisioning principal's hand-run
   * UPDATE in the kit runbook's section 8.4; no route writes it). The review is
   * the flag's READER: a home whose reception can see every group's requests
   * says so on the same sheet that says who reception is. ⭐ board 2732 M2
   * (ADR-0008): since then the flag ALSO discloses every request's NOTES to that
   * home's reception and admin, and the sheet's column says so (ruling RE).
   */
  crossGroupEnabled: boolean;
  crossGroupEnabledAt: string | null;
  /** The home's acts since `eventsSince` (the review's own quarter), newest first. */
  events: AccessReviewEvent[];
};

export type AccessReview = {
  /** ISO timestamp the report was built. */
  generatedAt: string;
  dormantAfterDays: number;
  /** ISO instant the acts section starts at: `generatedAt` minus `EVENTS_WINDOW_DAYS`. */
  eventsSince: string;
  homes: AccessReviewHome[];
};

/** How far back the acts section reaches: a quarter, the review's own cadence
 *  (plan Step 7 — "since the last quarter"). */
export const EVENTS_WINDOW_DAYS = 92;

type Row = {
  home_id: string | number;
  home_name: string;
  cross_group_requests_enabled: boolean;
  cross_group_requests_enabled_at: Date | string | null;
  nurse_id: string | number;
  display_name: string;
  email: string;
  role: PortalRole;
  group_label: string | null;
  has_pin: boolean;
  active: boolean;
  last_active_at: Date | string | null;
  device_count: string | number;
};

type EventRow = {
  home_id: string | number;
  trail: 'account' | 'staff';
  kind: string;
  actor: string;
  actor_nurse_id: string | number | null;
  target: string;
  at: Date | string;
};

/**
 * One row per nurse — active AND deactivated — with their home, last
 * activity and device count. Ordered home, then still-active first, then
 * name, so the emailed table reads the way the administrator thinks about
 * it and the removed people group together at the foot of each home.
 *
 * Note the LEFT JOINs: a nurse who has never signed in must still appear
 * (she is the most interesting row on the sheet — access granted and never
 * used), so anything that would turn these into inner joins is a bug.
 */
export async function buildAccessReview(pool: pg.Pool, now: Date): Promise<AccessReview> {
  return buildReview(pool, now, null);
}

/**
 * ⭐ T2075 M5 (plan Step 7, A7) — THE HOME'S OWN REVIEW, for the admin door's
 * `GET /api/admin/access-review`. The SAME builder as the operators' cross-home
 * report with `home_id = $1` in every WHERE (S3): one home, its accounts, its
 * flag and its acts. Two functions, never an optional parameter at the route,
 * so a caller cannot forget the scope by omission.
 */
export async function buildAccessReviewForHome(pool: pg.Pool, homeId: number, now: Date): Promise<AccessReview> {
  return buildReview(pool, now, homeId);
}

async function buildReview(pool: pg.Pool, now: Date, homeId: number | null): Promise<AccessReview> {
  // ⛔ `$1::bigint IS NULL OR h.home_id = $1` — the cross-home report binds null
  // and the admin door binds its session's home; never string interpolation of
  // the predicate (the `setNurseActiveScoped` shape in ops-repo.ts).
  const { rows } = await pool.query<Row>(
    `SELECT h.home_id                                AS home_id,
            h.name                                   AS home_name,
            h.cross_group_requests_enabled,
            h.cross_group_requests_enabled_at,
            n.nurse_id,
            n.display_name,
            n.email,
            n.role,
            n.group_label,
            (n.pin_hash IS NOT NULL)                 AS has_pin,
            n.active,
            MAX(s.last_active_at)                    AS last_active_at,
            COUNT(DISTINCT dn.device_id)             AS device_count
       FROM portal.nurses n
       JOIN portal.homes h        ON h.home_id = n.home_id
       LEFT JOIN portal.sessions s      ON s.nurse_id = n.nurse_id
       LEFT JOIN portal.device_nurses dn ON dn.nurse_id = n.nurse_id
      WHERE ($1::bigint IS NULL OR h.home_id = $1)
      GROUP BY h.home_id, h.name, h.cross_group_requests_enabled, h.cross_group_requests_enabled_at,
               n.nurse_id, n.display_name, n.email, n.role, n.group_label, n.pin_hash, n.active, n.home_id
      ORDER BY n.home_id, n.active DESC, n.display_name`,
    [homeId],
  );

  const eventsSince = new Date(now.getTime() - EVENTS_WINDOW_DAYS * 86_400_000);
  /**
   * ⛔ TWO QUERIES, NEVER A UNION, and never a subquery (pg-mem types a scalar
   * subquery as an array — memory). The account trail carries no home_id of its
   * own, so it is joined to `portal.nurses` for the home; the staff trail
   * carries its `home_id` directly (v13, no FK). Both are bounded by the
   * review's own quarter and ordered newest first.
   */
  const { rows: accountEvents } = await pool.query<EventRow>(
    `SELECT n.home_id, 'account' AS trail, e.event AS kind, e.actor, e.actor_nurse_id,
            e.nurse_email AS target, e.created_at AS at
       FROM portal.nurse_access_events e
       JOIN portal.nurses n ON n.nurse_id = e.nurse_id
      WHERE ($1::bigint IS NULL OR n.home_id = $1) AND e.created_at >= $2
      ORDER BY e.created_at DESC, e.event_id DESC`,
    [homeId, eventsSince],
  );
  const { rows: staffEvents } = await pool.query<EventRow>(
    `SELECT e.home_id, 'staff' AS trail, e.event AS kind, e.actor, e.actor_nurse_id,
            'staff:' || e.staff_id::text AS target, e.created_at AS at
       FROM portal.staff_events e
      WHERE ($1::bigint IS NULL OR e.home_id = $1) AND e.created_at >= $2
      ORDER BY e.created_at DESC, e.event_id DESC`,
    [homeId, eventsSince],
  );

  const dormantBefore = now.getTime() - DORMANT_AFTER_DAYS * 86_400_000;
  /**
   * ⛔ KEYED ON `home_id`, NOT ON `home_name` — this line IS the fix.
   *
   * Adding `homeId` to the emitted entry while leaving this map keyed on the
   * name does NOT work and looks like it does: two same-named homes still
   * collapse into ONE entry, now carrying the FIRST home's id, so the kiosk
   * attributes home B's nurses to home A's row and renders B empty. That is
   * strictly worse than the refusal it replaces.
   *
   * The value carries `homeName` because the entry still renders it; the KEY is
   * the identity.
   */
  const byHome = new Map<
    number,
    {
      homeName: string;
      crossGroupEnabled: boolean;
      crossGroupEnabledAt: string | null;
      nurses: AccessReviewNurse[];
      events: AccessReviewEvent[];
    }
  >();

  for (const r of rows) {
    const lastActive = r.last_active_at === null ? null : new Date(r.last_active_at);
    const nurse: AccessReviewNurse = {
      // ⚠️ `Number(...)`, not a bare read: `portal.nurses.nurse_id` is BIGINT,
      // which node-postgres hands back as a STRING unless the int8 parser in
      // core/pg-types.ts is registered — and this repo has a recorded boot path
      // that loses that registration. `device_count` a few lines down already
      // defends the same way for the same reason; a raw string here would reach
      // the kiosk's projection, which requires a number, and be DROPPED.
      nurseId: Number(r.nurse_id),
      displayName: r.display_name,
      email: r.email,
      hasSignedIn: lastActive !== null,
      hasPin: r.has_pin === true,
      lastActiveAt: lastActive === null ? null : lastActive.toISOString(),
      deviceCount: Number(r.device_count),
      dormant: lastActive === null || lastActive.getTime() < dormantBefore,
      active: r.active === true,
      role: r.role,
      groupLabel: r.group_label ?? null,
    };
    // ⚠️ `Number(...)` for the same reason `nurseId` above takes it: `home_id`
    // is BIGINT and node-postgres returns a STRING unless core/pg-types.ts's
    // int8 parser is registered, and this repo has a recorded boot path that
    // loses that registration. A string key here would also make every row its
    // own group only by accident of stringification.
    const rowHomeId = Number(r.home_id);
    const entry = byHome.get(rowHomeId);
    if (entry) entry.nurses.push(nurse);
    else {
      byHome.set(rowHomeId, {
        homeName: r.home_name,
        crossGroupEnabled: r.cross_group_requests_enabled === true,
        crossGroupEnabledAt:
          r.cross_group_requests_enabled_at === null || r.cross_group_requests_enabled_at === undefined
            ? null
            : new Date(r.cross_group_requests_enabled_at).toISOString(),
        nurses: [nurse],
        events: [],
      });
    }
  }
  // The two trails, merged newest first per home. An event for a home with no
  // nurse row cannot exist (the account trail joins nurses; the staff trail's
  // home holds its admin), so an unmatched home is dropped rather than invented.
  const merged = [...accountEvents, ...staffEvents].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );
  for (const e of merged) {
    const entry = byHome.get(Number(e.home_id));
    if (!entry) continue;
    entry.events.push({
      trail: e.trail,
      kind: e.kind,
      actor: e.actor,
      actorNurseId: e.actor_nurse_id === null || e.actor_nurse_id === undefined ? null : Number(e.actor_nurse_id),
      target: e.target,
      at: new Date(e.at).toISOString(),
    });
  }

  return {
    generatedAt: now.toISOString(),
    dormantAfterDays: DORMANT_AFTER_DAYS,
    eventsSince: eventsSince.toISOString(),
    homes: [...byHome.entries()].map(([id, v]) => ({
      homeId: id,
      homeName: v.homeName,
      nurses: v.nurses,
      crossGroupEnabled: v.crossGroupEnabled,
      crossGroupEnabledAt: v.crossGroupEnabledAt,
      events: v.events,
    })),
  };
}

/**
 * ⭐ T2075 M5 — THE ROSTER SHEET GAINS FOUR COLUMNS (plan Step 7, A7, S17):
 * `Sign-in type` and `Group` beside the name, and the home's R-9 state and its
 * date (`Sees every group, with notes` / `Since`) on every row of that home,
 * because a flat sheet has no home header and the reviewer reads one row at a
 * time. The decision column stays LAST and blank. `access-review.spec.ts` pins
 * the count. ⭐ board 2732 M2 (ruling RE, ADR-0008): the header was `Sees every
 * group` until the switch also disclosed notes; the consent is read in this
 * column, so the column says what it now grants. The comma is quoted by
 * `csvCell`, so the column count is unchanged.
 */
const CSV_HEADER = [
  'Home',
  'Nurse',
  'Work email',
  'Sign-in type',
  'Group',
  'Sees every group, with notes',
  'Since',
  'Last used the portal',
  'Devices',
  'Can sign in',
  'Flag',
  'Still needs access?',
];

/** The acts section's header: what changed, who did it, to what, when. */
const EVENTS_HEADER = ['Home', 'When', 'Who', 'What', 'To'];

/** The reviewer's words for a trail kind. A kind this map does not know is
 *  printed as stored, never dropped: a review that hid an act would be worse
 *  than one that named it oddly. */
const EVENT_WORDS: Readonly<Record<string, string>> = {
  added: 'added',
  deactivated: 'access removed',
  reactivated: 'access restored',
  pin_reset: 'PIN reset',
  renamed: 'renamed',
  regrouped: 'moved group',
};

/**
 * The emailed table. By default the final column is EMPTY — it is the
 * whole point of the exercise: the administrator writes yes or no next to
 * each name and sends it back. Everything to its left is what we know;
 * that column is what only they know.
 *
 * `decisionFor` (2026-07-21 review round) lets the review screen fill that
 * column from its on-screen Keep/Remove marks, so a reviewer who ticked
 * the table digitally exports what they ticked instead of re-writing it.
 * The pure-builder shape is deliberate: the client bundle imports this
 * function directly (the pg import above is type-only), so the on-screen
 * export and the API export cannot drift.
 */
export function accessReviewCsv(
  report: AccessReview,
  decisionFor?: (homeName: string, email: string) => string,
): string {
  const lines = [CSV_HEADER.map(csvCell).join(',')];

  for (const home of report.homes) {
    for (const n of home.nurses) {
      const decision = decisionFor ? decisionFor(home.homeName, n.email) : '';
      lines.push(
        [
          csvCell(home.homeName),
          csvCell(n.displayName),
          csvCell(n.email),
          csvCell(n.role),
          csvCell(n.groupLabel ?? ''),
          csvCell(home.crossGroupEnabled ? 'yes' : 'no'),
          csvCell(home.crossGroupEnabledAt === null ? '' : home.crossGroupEnabledAt.slice(0, 10)),
          csvCell(n.lastActiveAt === null ? 'never signed in' : n.lastActiveAt.slice(0, 10)),
          csvCell(String(n.deviceCount)),
          // ⛔ Without this column a deactivated nurse is INDISTINGUISHABLE
          // from an active one in the exported sheet — which would make the
          // widening above worse than useless, since the reviewer would read
          // removed staff as current staff.
          csvCell(n.active ? 'yes' : 'no - access removed'),
          csvCell(n.dormant ? `no activity in ${String(report.dormantAfterDays)} days` : ''),
          decision === '' ? '' : csvCell(decision),
        ].join(','),
      );
    }
  }

  /**
   * ⭐ T2075 M5 — THE ACTS SECTION, after a blank line: every account act and
   * every roster act on each home since `eventsSince`, newest first. The actor
   * is printed AS STORED (`admin:<id>` from the admin door, an operator's name
   * from the kiosk), because the sheet is the record and a prettified actor
   * would be a second spelling of one fact.
   * ⭐ board 2732 M4 (ruling RC, default): a name added from the request form
   * prints `nurse:<id>` | `staff added` | `staff:<id>`, as stored. ⚠️ The sheet
   * carries neither nurse ids nor staff ids elsewhere, so the reader sees two
   * ids it cannot resolve (today's `admin:<id>` rows are the same), and an ADMIN
   * adding through the form files `nurse:<id>` where the same admin on the Staff
   * tab files `admin:<id>`: two spellings of one account. Resolving them at read
   * time was RC's alternative, not taken.
   */
  const acts = report.homes.flatMap((home) =>
    home.events.map((e) =>
      [
        csvCell(home.homeName),
        csvCell(e.at.slice(0, 16).replace('T', ' ')),
        csvCell(e.actor),
        csvCell(`${e.trail === 'account' ? 'sign-in' : 'staff'} ${EVENT_WORDS[e.kind] ?? e.kind}`),
        csvCell(e.target),
      ].join(','),
    ),
  );
  lines.push('', EVENTS_HEADER.map(csvCell).join(','), ...acts);

  return lines.join('\n') + '\n';
}
